/**
 * ハイライトリールの地図
 *
 * リールを開いている間はマウントしたままにする。
 * シーンごとにマウントし直すと Google Maps のマップロードが都度発生してしまうため。
 */

import { AdvancedMarker, useMap } from '@vis.gl/react-google-maps'
import { useEffect, useMemo, useRef } from 'react'

import { GoogleMapCanvas } from '@/components/shared/map/google-map-canvas'
import { GooglePolyline } from '@/components/shared/map/google-polyline'
import { calculateDistance } from '@/lib/geo-utils'

import type { ChapterScene } from './trip-reel-scenes'

/** 出発地に留まって章タイトルを読ませる時間 */
const TRAVEL_DELAY_MS = 700
/** 軌跡をなぞりきるまでの時間 */
const TRAVEL_MS = 2700

/** 移動の全体像が収まる程度のズーム。道中はこの値で固定する */
const zoomForDistance = (km: number | null): number => {
  if (km === null || km < 1) return 15
  if (km < 3) return 14
  if (km < 8) return 13
  if (km < 20) return 12
  if (km < 50) return 11
  if (km < 150) return 10
  if (km < 400) return 8
  if (km < 1000) return 7
  return 6
}

const chapterPath = (scene: ChapterScene | null): google.maps.LatLngLiteral[] => {
  if (!scene) return []
  if (scene.legPath.length > 1) return scene.legPath
  return [scene.fromPosition, scene.position].filter((p): p is google.maps.LatLngLiteral => p !== null)
}

/** 各点までの累積距離（km） */
const cumulativeDistances = (path: google.maps.LatLngLiteral[]): number[] => {
  const acc = [0]
  for (let i = 1; i < path.length; i += 1) {
    const a = path.at(i - 1)
    const b = path.at(i)
    const prev = acc.at(-1) ?? 0
    acc.push(a && b ? prev + calculateDistance(a.lat, a.lng, b.lat, b.lng) : prev)
  }
  return acc
}

/** 軌跡上を 0..1 の割合で辿った地点。点が疎でも区間内を線形補間するので滑らかに動く */
const pointAt = (path: google.maps.LatLngLiteral[], acc: number[], ratio: number): google.maps.LatLngLiteral | null => {
  const total = acc.at(-1) ?? 0
  if (total <= 0) return path.at(0) ?? null

  const target = total * ratio
  for (let i = 1; i < acc.length; i += 1) {
    const from = acc.at(i - 1) ?? 0
    const to = acc.at(i) ?? 0
    if (target > to) continue

    const a = path.at(i - 1)
    const b = path.at(i)
    if (!a || !b) break

    const span = to - from
    const r = span === 0 ? 0 : (target - from) / span
    return { lat: a.lat + (b.lat - a.lat) * r, lng: a.lng + (b.lng - a.lng) * r }
  }
  return path.at(-1) ?? null
}

const easeInOut = (t: number): number => (t < 0.5 ? 4 * t ** 3 : 1 - (-2 * t + 2) ** 3 / 2)

type ChapterMapProps = {
  scene: ChapterScene | null
  /** 到着地マーカーにはめ込む写真。先読みが間に合わなければ null */
  arrivalPhotoUrl?: string | null
  initialCenter?: google.maps.LatLngLiteral
  /** 再生速度の倍率 */
  speed?: number
}

export const ReelChapterMap = ({ scene, arrivalPhotoUrl, initialCenter, speed = 1 }: ChapterMapProps) => {
  const isTraveling = useMemo(() => chapterPath(scene).length > 1, [scene])

  return (
    <div className="relative h-full w-full">
      <GoogleMapCanvas
        className="h-full w-full"
        defaultCenter={initialCenter}
        defaultZoom={12}
        gestureHandling="none"
        zoomControl={false}
      >
        <ChapterCamera scene={scene} arrivalPhotoUrl={arrivalPhotoUrl ?? null} speed={speed} />
      </GoogleMapCanvas>
      {/* カメラは常に現在地を中心に置くので、画面中央に重ねるだけで現在地マーカーになる */}
      {isTraveling && <TravelerDot />}
    </div>
  )
}

/** 移動中の現在地。地図が下を流れ、この点が軌跡をなぞっていく */
const TravelerDot = () => (
  <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
    <span className="relative flex items-center justify-center">
      <span className="absolute size-9 animate-ping rounded-full bg-amber-400/40" />
      <span className="size-4 rounded-full border-2 border-white bg-amber-500 shadow-lg" />
    </span>
  </div>
)

const ChapterCamera = ({
  scene,
  arrivalPhotoUrl,
  speed,
}: {
  scene: ChapterScene | null
  arrivalPhotoUrl: string | null
  speed: number
}) => {
  const map = useMap()
  const path = useMemo(() => chapterPath(scene), [scene])
  const acc = useMemo(() => cumulativeDistances(path), [path])

  // 速度変更でアニメーションを作り直すと途中で先頭に戻ってしまうので、ref 経由で参照する
  const speedRef = useRef(speed)
  useEffect(() => {
    speedRef.current = speed
  }, [speed])

  useEffect(() => {
    if (!map || !scene) return

    const start = path.at(0) ?? scene.position
    if (!start) return

    // setZoom / setCenter はベクターマップだと内部のカメラアニメーションに乗ってしまい、
    // 毎フレームの指示が補間されて軌跡が直線に均される。moveCamera なら即時反映される
    // ズームは実際になぞる軌跡から測る（legDistanceKm は GPS が無いと null になる）
    map.moveCamera({ center: start, zoom: zoomForDistance(acc.at(-1) ?? scene.legDistanceKm) })
    if (path.length < 2) return

    let progress = 0
    let last = performance.now()
    let frame = 0
    const step = (now: number) => {
      progress += (now - last) * speedRef.current
      last = now

      const elapsed = progress - TRAVEL_DELAY_MS
      if (elapsed > 0) {
        const t = Math.min(elapsed / TRAVEL_MS, 1)
        const point = pointAt(path, acc, easeInOut(t))
        if (point) map.moveCamera({ center: point })
        if (t >= 1) return
      }
      frame = requestAnimationFrame(step)
    }
    frame = requestAnimationFrame(step)

    return () => cancelAnimationFrame(frame)
  }, [map, scene, path, acc])

  return (
    <>
      {path.length > 1 && (
        <GooglePolyline path={path} strokeColor="#f97316" strokeWeight={4} strokeOpacity={0.9} zIndex={1} />
      )}
      {scene?.fromPosition && (
        <AdvancedMarker position={scene.fromPosition} zIndex={10}>
          <DeparturePin label={scene.fromPlaceName} />
        </AdvancedMarker>
      )}
      {scene?.position && (
        <AdvancedMarker position={scene.position} zIndex={20}>
          <ArrivalPin label={scene.placeName} photoUrl={arrivalPhotoUrl} />
        </AdvancedMarker>
      )}
    </>
  )
}

const DeparturePin = ({ label }: { label: string | null }) => (
  <div className="flex flex-col items-center gap-1">
    <span className="size-4 rounded-full border-2 border-white bg-slate-400 shadow-md" />
    {label && (
      <span className="max-w-28 truncate rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-medium text-white">
        {label}
      </span>
    )}
  </div>
)

/** 到着地。写真をはめ込んだ大きめの丸で、どこに着いたのかを一目で分かるようにする */
const ArrivalPin = ({ label, photoUrl }: { label: string; photoUrl: string | null }) => (
  <div className="flex flex-col items-center gap-1">
    <span className="relative flex size-16 items-center justify-center rounded-full border-[3px] border-white bg-red-500 shadow-xl">
      {photoUrl && (
        <img src={photoUrl} alt="" aria-hidden className="size-full rounded-full object-cover" loading="eager" />
      )}
      <span className="absolute -bottom-1 size-3 rotate-45 border-r-[3px] border-b-[3px] border-white bg-red-500" />
    </span>
    <span className="mt-1 max-w-32 truncate rounded-full bg-black/75 px-2.5 py-1 text-xs font-bold text-white shadow-md">
      {label}
    </span>
  </div>
)
